<script setup>
import { ref, reactive, computed, watch, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import FollowUpMediaPicker from './FollowUpMediaPicker.vue';
import { isMoveStep, moveTargetFunnel, moveStageOptions } from './followUpMove';

const props = defineProps({
  step: {
    type: Object,
    required: true,
  },
  inboxes: {
    type: Array,
    default: () => [],
  },
  // União de templates de todos os inboxes elegíveis — usada só quando o
  // passo não tem uma inbox específica escolhida (ver templatesForInbox).
  templates: {
    type: Array,
    default: () => [],
  },
  // Mapa inbox_id -> templates daquela inbox. Quando presente e o passo
  // tem uma inbox específica escolhida, restringe o seletor de template
  // aos templates que realmente existem nela (evita a combinação
  // template-de-A-enviado-por-B que o processor descarta silenciosamente).
  templatesByInbox: {
    type: Object,
    default: null,
  },
  // Vozes oferecidas para o passo de áudio da IA: a primeira opção (vazia)
  // usa a voz já configurada no agente da inbox; as demais vêm dos
  // provedores de TTS e das vozes clonadas da conta (Voice Studio).
  voices: {
    type: Array,
    default: () => [],
  },
  // Destinos do passo "Mover card": funis da conta ({ id, name, stages }), o
  // funil desta régua e a etapa dona do passo.
  funnels: {
    type: Array,
    default: () => [],
  },
  funnelId: {
    type: [Number, String],
    default: null,
  },
  stageKey: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['save', 'cancel']);

const { t } = useI18n();

const { currentAccount } = useAccount();
// Passo "IA"/"tarefa" só existe no motor novo (FollowUps); no antigo o
// processor pula esse passo (follow_up_skipped 'engine_v2_step').
const engineV2 = computed(() => !!currentAccount.value?.follow_up_engine_v2);
// Conta fora do motor com passo IA/tarefa já salvo (ex.: motor desligado
// depois): a escolha aparece para o gestor poder voltar o passo a pronto.
const savedEngineStep = ['ai', 'task'].includes(props.step.mode);
const showModes = computed(() => engineV2.value || savedEngineStep);
const INTENTS = [
  'retomar',
  'valor',
  'duvida',
  'prova_social',
  'ultima_tentativa',
  'livre',
];
const HINT_MAX = 500;

const VARIABLES = [
  '{{primeiro_nome}}',
  '{{nome}}',
  '{{telefone}}',
  '{{email}}',
  '{{empresa}}',
];

// Atributos oferecidos para as variáveis do template. Os valores viajam para o
// backend (Funnels::TemplateVariableResolver), que os traduz para os mesmos
// tokens do modo Texto — uma convenção só.
const CONTACT_ATTRIBUTES = [
  'first_name',
  'name',
  'phone_number',
  'email',
  'company',
];

const BODY_VARIABLE_PATTERN = /\{\{\s*(\d+)\s*\}\}/g;

const localStep = reactive({ ...props.step });
// Passo sem mode/intent (fixture antiga ou conta no motor antigo) cai no
// comportamento de sempre: mensagem pronta, retomando a conversa. Só
// injeta os defaults no motor novo — fora dele o payload salvo não pode
// ganhar campo nenhum que o servidor não esperava antes desta tela existir.
if (engineV2.value) {
  if (!localStep.mode) localStep.mode = 'fixed';
  if (!localStep.intent) localStep.intent = 'retomar';
}
const isAiMode = computed(() => localStep.mode === 'ai');
const isTaskMode = computed(() => localStep.mode === 'task');
const isMoveMode = computed(() => isMoveStep(localStep));
if (isMoveMode.value && localStep.move_funnel_id === undefined) {
  localStep.move_funnel_id = null;
}
const setMode = mode => {
  localStep.mode = mode;
};
const days = ref(Math.floor((Number(props.step.delay_hours) || 0) / 24));
const hours = ref((Number(props.step.delay_hours) || 0) % 24);
// Passo "na hora": entregue no evento de entrada na etapa
// (Funnels::InstantStageFollowUpJob), não pelo cron da régua. Espera não existe
// nesse modo, então os campos de dias/horas sequer aparecem.
const onStageEnter = ref(!!props.step.on_stage_enter);
const messageTextareaRef = ref(null);

// Templates candidatos ao passo atual. Com uma inbox específica escolhida
// (inbox_id != null) e o mapa por-inbox disponível, restringe à lista
// daquela inbox — mesmo que vazia. Sem inbox específica ("Mesma caixa da
// conversa") ou sem o mapa disponível, cai na união (comportamento antigo).
const templatesForInbox = computed(() => {
  if (localStep.inbox_id == null || !props.templatesByInbox) {
    return props.templates;
  }
  return props.templatesByInbox[localStep.inbox_id] || [];
});

// Passo salvo como template sem templates disponíveis pro seu escopo atual
// (ex.: template excluído na Meta, ou inbox escolhida não tem nenhum): cai
// pra texto pra não renderizar um select vazio.
if (localStep.message_type === 'template' && !templatesForInbox.value.length) {
  localStep.message_type = 'text';
}

// Trocar a inbox do passo pode invalidar o template já escolhido (ele só
// existia na inbox anterior). Sem isso o Save salvaria uma combinação
// impossível que o processor descarta silenciosamente em produção.
watch(
  () => localStep.inbox_id,
  () => {
    if (localStep.message_type !== 'template') return;
    if (!templatesForInbox.value.length) {
      localStep.message_type = 'text';
      localStep.template_name = '';
      return;
    }
    const stillValid = templatesForInbox.value.some(
      template => template.name === localStep.template_name
    );
    if (!stillValid) {
      localStep.template_name = '';
    }
  }
);

const isVisible = ref(false);
onMounted(() => {
  nextTick(() => {
    isVisible.value = true;
  });
});

const isTemplate = computed(() => localStep.message_type === 'template');

// Caixas com janela de 24h (mesmo critério de Conversations::MessageWindowService):
// fora dela só template sai, e passo de texto/mídia/áudio vira tentativa que
// pausa o card depois de 3 vezes.
const hasSessionWindow = inbox =>
  inbox?.channel_type === 'Channel::Whatsapp' ||
  (inbox?.channel_type === 'Channel::TwilioSms' &&
    inbox?.medium === 'whatsapp');
// "Mesmo canal da conversa" pode cair em qualquer caixa da lista.
const showSessionWindowWarning = computed(() => {
  if (isMoveMode.value || isAiMode.value || isTaskMode.value) return false;
  if (isTemplate.value) return false;
  if (localStep.inbox_id == null) return props.inboxes.some(hasSessionWindow);
  return hasSessionWindow(
    props.inboxes.find(inbox => inbox.id === localStep.inbox_id)
  );
});

// Tipos que dependem de um arquivo da biblioteca de mídia (FollowUpMediaPicker).
const MEDIA_TYPES = ['image', 'document', 'video', 'audio'];
const isMedia = computed(() => MEDIA_TYPES.includes(localStep.message_type));
const isAiAudio = computed(() => localStep.message_type === 'ai_audio');
// Mesmo limite do backend (Messages::AudioSynthesisService::MAX_TEXT_LENGTH):
// acima disso a síntese de voz recusa o roteiro.
const AI_AUDIO_MAX = 4096;

const typeOptions = computed(() => {
  const options = [{ value: 'text', label: t('FUNNEL_TIMELINE.TYPE_TEXT') }];
  if (templatesForInbox.value.length) {
    options.push({
      value: 'template',
      label: t('FUNNEL_TIMELINE.TYPE_TEMPLATE'),
    });
  }
  options.push(
    { value: 'image', label: t('FUNNEL_TIMELINE.TYPE_IMAGE') },
    { value: 'document', label: t('FUNNEL_TIMELINE.TYPE_DOCUMENT') },
    { value: 'video', label: t('FUNNEL_TIMELINE.TYPE_VIDEO') },
    { value: 'audio', label: t('FUNNEL_TIMELINE.TYPE_AUDIO') },
    { value: 'ai_audio', label: t('FUNNEL_TIMELINE.TYPE_AI_AUDIO') }
  );
  return options;
});

// O select de tipo também oferece "Mover card", que no passo vira
// mode: 'move' (não é um tipo de mensagem). Voltar para um tipo de mensagem
// devolve o passo ao modo pronto.
const stepType = computed({
  get: () => (isMoveMode.value ? 'move' : localStep.message_type),
  set: value => {
    if (value === 'move') {
      localStep.mode = 'move';
      if (localStep.move_funnel_id === undefined)
        localStep.move_funnel_id = null;
      return;
    }
    if (isMoveMode.value) {
      if (engineV2.value) localStep.mode = 'fixed';
      else delete localStep.mode;
    }
    localStep.message_type = value;
  },
});

const ownFunnelTarget = computed(() => localStep.move_funnel_id == null);
const moveFunnelOptions = computed(() =>
  props.funnels.filter(funnel => funnel.id !== props.funnelId)
);
const moveStages = computed(() =>
  moveStageOptions(moveTargetFunnel(localStep, props.funnels, props.funnelId), {
    ownFunnel: ownFunnelTarget.value,
    stageKey: props.stageKey,
  })
);
// Trocar o funil de destino invalida a etapa escolhida no outro funil.
watch(
  () => localStep.move_funnel_id,
  () => {
    if (!isMoveMode.value) return;
    const stillValid = moveStages.value.some(
      stage => stage.key === localStep.move_stage_name
    );
    if (!stillValid) localStep.move_stage_name = '';
  }
);
const keepInSource = computed({
  get: () => localStep.move_keep_in_source === true,
  set: value => {
    localStep.move_keep_in_source = value;
  },
});

const pickMedia = asset => {
  localStep.media_asset_id = asset.id;
  localStep.media_name = asset.name;
  localStep.media_url = asset.url;
  localStep.media_type = asset.media_type;
};

// Trocar o tipo do passo pode deixar uma mídia escolhida antes órfã (ex.: era
// imagem e virou documento, texto, template ou áudio da IA) — sem isso o Save
// mandaria um media_asset_id que não bate com o message_type novo.
watch(
  () => localStep.message_type,
  type => {
    if (localStep.media_type && localStep.media_type !== type) {
      localStep.media_asset_id = null;
      localStep.media_name = '';
      localStep.media_url = '';
      localStep.media_type = '';
    }
  }
);

const scriptLength = computed(() => (localStep.content || '').length);

// A lista de vozes chega por HTTP (FunnelSettings.loadFollowUpVoices) e demora
// um tique: sem a opção padrão semeada aqui o seletor abriria VAZIO, dando a
// impressão de que o passo perdeu a voz. A opção vazia = voz do agente da caixa.
const voiceOptions = computed(() => {
  const list = props.voices || [];
  if (list.some(option => option.provider === '')) return list;

  return [
    { provider: '', voice: '', label: t('FUNNEL_TIMELINE.VOICE_AGENT') },
    ...list,
  ];
});

const onVoiceChange = value => {
  const [provider, voice] = value.split('|');
  localStep.tts_provider = provider;
  localStep.tts_voice = voice;
};

const selectedTemplate = computed(() =>
  templatesForInbox.value.find(
    template => template.name === localStep.template_name
  )
);

const templateBody = computed(() => {
  const body = (selectedTemplate.value?.components || []).find(
    component => (component.type || '').toUpperCase() === 'BODY'
  );
  return body?.text || '';
});

// Índices {{n}} declarados no CORPO, sem repetir. Cabeçalho e botões ficam de
// fora de propósito: nenhum template da base usa variável neles.
const templateVariables = computed(() => {
  const found = [...templateBody.value.matchAll(BODY_VARIABLE_PATTERN)].map(
    match => match[1]
  );
  return [...new Set(found)];
});

// Sem mapeamento a Meta recusa o envio com "#132000 Number of parameters does
// not match the expected number of params". Por isso o Salvar espera.
const missingVariables = computed(() =>
  templateVariables.value.filter(index => {
    const mapping = localStep.template_variables?.[index];
    return !mapping?.source || !String(mapping.value || '').trim();
  })
);

const ensureVariableSlots = () => {
  const current = { ...(localStep.template_variables || {}) };
  templateVariables.value.forEach(index => {
    if (!current[index]) {
      current[index] = { source: 'attribute', value: 'name', fallback: '' };
    }
  });
  // Trocar de template pode deixar mapeamento órfão de {{n}} que não existe
  // mais — ele iria junto no save e confundiria a leitura do passo.
  Object.keys(current).forEach(index => {
    if (!templateVariables.value.includes(index)) delete current[index];
  });
  localStep.template_variables = current;
};

watch(
  () => [localStep.template_name, localStep.message_type],
  () => {
    if (isTemplate.value) ensureVariableSlots();
  },
  { immediate: true }
);

const ATTRIBUTE_LABELS = {
  first_name: () => t('FUNNEL_TIMELINE.TEMPLATE_ATTRIBUTE_FIRST_NAME'),
  name: () => t('FUNNEL_TIMELINE.TEMPLATE_ATTRIBUTE_NAME'),
  phone_number: () => t('FUNNEL_TIMELINE.TEMPLATE_ATTRIBUTE_PHONE_NUMBER'),
  email: () => t('FUNNEL_TIMELINE.TEMPLATE_ATTRIBUTE_EMAIL'),
  company: () => t('FUNNEL_TIMELINE.TEMPLATE_ATTRIBUTE_COMPANY'),
};

const attributeLabel = attribute =>
  (ATTRIBUTE_LABELS[attribute] || ATTRIBUTE_LABELS.name)();

// Montado no script: chaves duplas dentro de interpolação confundem o parser
// de template do Vue.
const variableLabel = index => `{{${index}}}`;

const previewValue = index => {
  const mapping = localStep.template_variables?.[index];
  if (!mapping) return `{{${index}}}`;
  return mapping.source === 'attribute'
    ? attributeLabel(mapping.value || 'name')
    : mapping.value || `{{${index}}}`;
};

const templatePreview = computed(() =>
  templateBody.value.replace(BODY_VARIABLE_PATTERN, (_, index) =>
    previewValue(index)
  )
);

const canSave = computed(() => {
  if (isMoveMode.value) {
    return moveStages.value.some(
      stage => stage.key === localStep.move_stage_name
    );
  }
  if (isAiMode.value) return (localStep.hint || '').length <= HINT_MAX;
  if (isTaskMode.value) return !!localStep.task_text?.trim();
  if (isTemplate.value) {
    return !!localStep.template_name && missingVariables.value.length === 0;
  }
  if (isMedia.value) return !!localStep.media_asset_id;
  if (isAiAudio.value) {
    return !!localStep.content?.trim() && scriptLength.value <= AI_AUDIO_MAX;
  }
  return !!localStep.content?.trim();
});

const insertVariable = variable => {
  const el = messageTextareaRef.value;
  const currentContent = localStep.content || '';
  const start = el?.selectionStart ?? currentContent.length;
  const end = el?.selectionEnd ?? currentContent.length;

  localStep.content =
    currentContent.slice(0, start) + variable + currentContent.slice(end);

  nextTick(() => {
    if (!el) return;
    const cursor = start + variable.length;
    el.focus();
    el.setSelectionRange(cursor, cursor);
  });
};

// Passo "Mover card" leva só o que o servidor usa: sem campos de mensagem
// sobrando de quando o passo era texto/mídia. Nunca é imediato.
const MESSAGE_FIELDS = [
  'message_type',
  'content',
  'inbox_id',
  'template_name',
  'template_variables',
  'media_asset_id',
  'media_name',
  'media_url',
  'media_type',
  'tts_provider',
  'tts_voice',
  'intent',
  'hint',
  'task_text',
];

const moveStepPayload = wait => {
  const payload = {
    ...localStep,
    mode: 'move',
    on_stage_enter: false,
    delay_hours: wait,
    wait_hours: wait,
  };
  MESSAGE_FIELDS.forEach(field => delete payload[field]);
  if (ownFunnelTarget.value) {
    payload.move_funnel_id = null;
    delete payload.move_keep_in_source;
  } else {
    payload.move_keep_in_source = keepInSource.value;
  }
  return payload;
};

const handleSave = () => {
  if (!canSave.value) return;
  const wait =
    onStageEnter.value && !isMoveMode.value
      ? 0
      : (Number(days.value) || 0) * 24 + (Number(hours.value) || 0);

  if (isMoveMode.value) {
    emit('save', moveStepPayload(wait));
    return;
  }

  const payload = {
    ...localStep,
    on_stage_enter: onStageEnter.value,
    delay_hours: wait,
  };
  // Passo que era "Mover card" e voltou a ser mensagem.
  delete payload.move_funnel_id;
  delete payload.move_stage_name;
  delete payload.move_keep_in_source;

  // Fora do motor novo o passo pronto sai com o payload idêntico ao de
  // sempre: o validador do servidor só entra em wait_errors quando a chave
  // wait_hours existe, e um passo antigo com espera > 90 dias (hoje válido)
  // passaria a ser recusado se a gente mandasse essa chave sem querer. Passo
  // IA/tarefa mantido segue o caminho do motor novo (o modo precisa ir junto).
  if (!engineV2.value && !isAiMode.value && !isTaskMode.value) {
    delete payload.mode;
    delete payload.intent;
    delete payload.hint;
    delete payload.task_text;
    emit('save', payload);
    return;
  }

  // wait_hours é o nome que o motor novo usa pro mesmo valor — os dois
  // viajam juntos pra servir conta antiga e nova sem quebrar nenhuma.
  payload.wait_hours = wait;
  if (!isAiMode.value) {
    delete payload.intent;
    delete payload.hint;
  }
  if (!isTaskMode.value) {
    delete payload.task_text;
  }

  emit('save', payload);
};

const handleCancel = () => {
  emit('cancel');
};
</script>

<template>
  <div
    class="grid overflow-hidden transition-all duration-200 ease-in-out"
    :class="
      isVisible ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
    "
  >
    <div class="min-h-0 space-y-3 overflow-hidden p-3">
      <label
        v-if="!isMoveMode"
        class="flex cursor-pointer items-start gap-2.5 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5"
      >
        <input
          v-model="onStageEnter"
          type="checkbox"
          data-testid="follow-up-step-editor-on-stage-enter"
          class="mt-0.5 size-4 shrink-0 accent-n-brand"
        />
        <span class="flex flex-col gap-0.5">
          <span
            class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
          >
            <span class="i-lucide-zap size-3.5 text-n-brand" />
            {{ t('FUNNEL_TIMELINE.ON_STAGE_ENTER') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('FUNNEL_TIMELINE.ON_STAGE_ENTER_HINT') }}
          </span>
        </span>
      </label>

      <div v-if="!onStageEnter || isMoveMode" class="grid grid-cols-2 gap-3">
        <div>
          <label class="mb-1 block text-xs font-medium text-n-slate-11">
            {{ t('FUNNEL_TIMELINE.DELAY_DAYS') }}
          </label>
          <input
            v-model.number="days"
            type="number"
            min="0"
            data-testid="follow-up-step-editor-days"
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-n-slate-11">
            {{ t('FUNNEL_TIMELINE.DELAY_HOURS') }}
          </label>
          <input
            v-model.number="hours"
            type="number"
            min="0"
            data-testid="follow-up-step-editor-hours"
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
        <p v-if="engineV2" class="col-span-2 text-xs text-n-slate-10">
          {{ t('FUNNEL_TIMELINE.WAIT_SINCE_CUSTOMER') }}
        </p>
      </div>

      <div v-if="showModes" class="flex gap-2">
        <button
          v-for="mode in ['ai', 'fixed', 'task']"
          :key="mode"
          type="button"
          :data-test="`mode-${mode}`"
          class="flex-1 rounded-lg border px-3 py-2 text-xs font-medium"
          :class="
            localStep.mode === mode || (mode === 'fixed' && isMoveMode)
              ? 'border-n-brand bg-n-brand/10 text-n-brand'
              : 'border-n-weak text-n-slate-11'
          "
          @click="setMode(mode)"
        >
          {{ t(`FUNNEL_TIMELINE.MODE_${mode.toUpperCase()}`) }}
        </button>
      </div>

      <div v-if="isAiMode" class="space-y-2">
        <label class="text-xs text-n-slate-11">
          {{ t('FUNNEL_TIMELINE.INTENT_LABEL') }}
        </label>
        <select
          v-model="localStep.intent"
          data-test="intent-select"
          class="reset-base w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
        >
          <option v-for="intent in INTENTS" :key="intent" :value="intent">
            {{ t(`FUNNEL_TIMELINE.INTENT_${intent.toUpperCase()}`) }}
          </option>
        </select>
        <textarea
          v-model="localStep.hint"
          data-test="hint-input"
          :maxlength="HINT_MAX"
          rows="2"
          :placeholder="t('FUNNEL_TIMELINE.HINT_PLACEHOLDER')"
          class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
        />
        <p class="text-xs text-n-slate-10">
          {{ t('FUNNEL_TIMELINE.AI_MODE_HELP') }}
        </p>
      </div>

      <div v-if="isTaskMode" class="space-y-2">
        <input
          v-model="localStep.task_text"
          data-test="task-input"
          :placeholder="t('FUNNEL_TIMELINE.TASK_PLACEHOLDER')"
          class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
        />
      </div>

      <template v-if="!isAiMode && !isTaskMode">
        <div class="grid grid-cols-2 gap-3">
          <div class="relative w-full">
            <select
              v-model="stepType"
              data-testid="follow-up-step-editor-type"
              class="reset-base w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 pr-9 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
            >
              <option
                v-for="option in typeOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
              <option value="move">
                {{ t('FUNNEL_TIMELINE.TYPE_MOVE') }}
              </option>
            </select>
            <span
              class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
            />
          </div>
          <div v-if="!isMoveMode" class="relative w-full">
            <select
              v-model="localStep.inbox_id"
              data-testid="follow-up-step-editor-inbox"
              class="reset-base w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 pr-9 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
            >
              <option :value="null">
                {{ t('FUNNEL_TIMELINE.SAME_INBOX') }}
              </option>
              <option
                v-for="inbox in inboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
            <span
              class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
            />
          </div>
        </div>

        <p
          v-if="showSessionWindowWarning"
          data-testid="follow-up-step-editor-session-window"
          class="flex items-start gap-2 rounded-lg bg-n-amber-3 px-3 py-2 text-xs text-n-amber-11"
        >
          <span class="i-lucide-clock-alert mt-0.5 size-3.5 shrink-0" />
          {{ t('FUNNEL_TIMELINE.SESSION_WINDOW_WARNING') }}
        </p>

        <div v-if="isMoveMode" class="space-y-3">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ t('FUNNEL_TIMELINE.MOVE_FUNNEL') }}
              </label>
              <div class="relative w-full">
                <select
                  v-model="localStep.move_funnel_id"
                  data-testid="follow-up-step-editor-move-funnel"
                  class="reset-base w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 pr-9 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
                >
                  <option :value="null">
                    {{ t('FUNNEL_TIMELINE.MOVE_SAME_FUNNEL') }}
                  </option>
                  <option
                    v-for="funnel in moveFunnelOptions"
                    :key="funnel.id"
                    :value="funnel.id"
                  >
                    {{ funnel.name }}
                  </option>
                </select>
                <span
                  class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
                />
              </div>
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ t('FUNNEL_TIMELINE.MOVE_STAGE') }}
              </label>
              <div class="relative w-full">
                <select
                  v-model="localStep.move_stage_name"
                  data-testid="follow-up-step-editor-move-stage"
                  class="reset-base w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 pr-9 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
                >
                  <option value="" disabled>
                    {{ t('FUNNEL_TIMELINE.MOVE_STAGE_PLACEHOLDER') }}
                  </option>
                  <option
                    v-for="stage in moveStages"
                    :key="stage.key"
                    :value="stage.key"
                  >
                    {{ stage.label }}
                  </option>
                </select>
                <span
                  class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
                />
              </div>
            </div>
          </div>
          <div v-if="!ownFunnelTarget" class="flex flex-col gap-1.5">
            <label
              class="flex cursor-pointer items-center gap-2 text-sm text-n-slate-12"
            >
              <input
                v-model="keepInSource"
                type="radio"
                :value="false"
                data-testid="follow-up-step-editor-move-remove"
                class="size-4 accent-n-brand"
              />
              {{ t('FUNNEL_TIMELINE.MOVE_REMOVE_FROM_SOURCE') }}
            </label>
            <label
              class="flex cursor-pointer items-center gap-2 text-sm text-n-slate-12"
            >
              <input
                v-model="keepInSource"
                type="radio"
                :value="true"
                data-testid="follow-up-step-editor-move-keep"
                class="size-4 accent-n-brand"
              />
              {{ t('FUNNEL_TIMELINE.MOVE_KEEP_IN_SOURCE') }}
            </label>
          </div>
          <p class="text-xs text-n-slate-10">
            {{ t('FUNNEL_TIMELINE.MOVE_HELP') }}
          </p>
        </div>

        <div v-else-if="isTemplate" class="relative w-full">
          <select
            v-model="localStep.template_name"
            data-testid="follow-up-step-editor-template"
            class="reset-base w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 pr-9 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
          >
            <option
              v-for="template in templatesForInbox"
              :key="template.name"
              :value="template.name"
            >
              {{ template.name }}
            </option>
          </select>
          <span
            class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
          />
        </div>

        <div v-else class="space-y-1.5">
          <textarea
            ref="messageTextareaRef"
            v-model="localStep.content"
            rows="4"
            :data-testid="
              isAiAudio
                ? 'follow-up-step-editor-script'
                : 'follow-up-step-editor-message'
            "
            :maxlength="isAiAudio ? AI_AUDIO_MAX : undefined"
            class="w-full resize-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
            :placeholder="
              isAiAudio
                ? t('FUNNEL_TIMELINE.SCRIPT_PLACEHOLDER')
                : t('FUNNEL_TIMELINE.MESSAGE')
            "
          />
          <p v-if="isAiAudio" class="text-xs text-n-slate-10">
            {{ scriptLength }}/{{ AI_AUDIO_MAX }} ·
            {{ t('FUNNEL_TIMELINE.SCRIPT_HELP') }}
          </p>
          <div class="flex flex-wrap items-center gap-1.5">
            <span class="text-xs text-n-slate-10">
              {{ t('FUNNEL_TIMELINE.VARIABLES_HINT') }}
            </span>
            <button
              v-for="variable in VARIABLES"
              :key="variable"
              type="button"
              :data-testid="`follow-up-step-editor-chip-${variable.replace(/[{}]/g, '')}`"
              class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs hover:bg-n-brand/20"
              @click="insertVariable(variable)"
            >
              {{ variable }}
            </button>
          </div>
        </div>

        <FollowUpMediaPicker
          v-if="isMedia && !isMoveMode"
          :media-type="localStep.message_type"
          :selected="localStep.media_asset_id"
          @pick="pickMedia"
        />

        <div v-if="isAiAudio && !isMoveMode" class="relative w-full">
          <select
            data-testid="follow-up-step-editor-voice"
            class="reset-base w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 pr-9 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
            :value="`${localStep.tts_provider || ''}|${localStep.tts_voice || ''}`"
            @change="onVoiceChange($event.target.value)"
          >
            <option
              v-for="voice in voiceOptions"
              :key="`${voice.provider}|${voice.voice}`"
              :value="`${voice.provider}|${voice.voice}`"
            >
              {{ voice.label }}
            </option>
          </select>
          <span
            class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
          />
        </div>

        <div
          v-if="isTemplate && !isMoveMode && templateVariables.length"
          class="space-y-2"
        >
          <p class="text-xs font-medium text-n-slate-11">
            {{ t('FUNNEL_TIMELINE.TEMPLATE_VARIABLES_TITLE') }}
          </p>
          <div
            v-for="index in templateVariables"
            :key="index"
            class="grid grid-cols-[3rem_1fr_1fr] items-center gap-2"
            :data-testid="`follow-up-step-editor-variable-${index}`"
          >
            <span class="font-mono text-xs text-n-slate-11">
              {{ variableLabel(index) }}
            </span>
            <select
              v-model="localStep.template_variables[index].source"
              :data-testid="`follow-up-step-editor-variable-source-${index}`"
              class="reset-base w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
            >
              <option value="attribute">
                {{ t('FUNNEL_TIMELINE.TEMPLATE_SOURCE_ATTRIBUTE') }}
              </option>
              <option value="text">
                {{ t('FUNNEL_TIMELINE.TEMPLATE_SOURCE_TEXT') }}
              </option>
            </select>
            <select
              v-if="localStep.template_variables[index].source === 'attribute'"
              v-model="localStep.template_variables[index].value"
              :data-testid="`follow-up-step-editor-variable-value-${index}`"
              class="reset-base w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
            >
              <option
                v-for="attribute in CONTACT_ATTRIBUTES"
                :key="attribute"
                :value="attribute"
              >
                {{ attributeLabel(attribute) }}
              </option>
            </select>
            <input
              v-else
              v-model="localStep.template_variables[index].value"
              :data-testid="`follow-up-step-editor-variable-value-${index}`"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="t('FUNNEL_TIMELINE.TEMPLATE_TEXT_PLACEHOLDER')"
            />
            <span />
            <input
              v-if="localStep.template_variables[index].source === 'attribute'"
              v-model="localStep.template_variables[index].fallback"
              :data-testid="`follow-up-step-editor-variable-fallback-${index}`"
              class="col-span-2 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="t('FUNNEL_TIMELINE.TEMPLATE_FALLBACK_PLACEHOLDER')"
            />
          </div>
          <p
            class="whitespace-pre-line rounded-lg bg-n-alpha-2 px-3 py-2 text-xs text-n-slate-11"
            data-testid="follow-up-step-editor-template-preview"
          >
            {{ templatePreview }}
          </p>
        </div>
      </template>

      <div
        class="flex items-center justify-end gap-2 border-t border-n-weak pt-3"
      >
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          data-testid="follow-up-step-editor-cancel"
          :label="t('FUNNEL_TIMELINE.CANCEL')"
          @click="handleCancel"
        />
        <Button
          variant="solid"
          color="blue"
          size="sm"
          data-testid="follow-up-step-editor-save"
          data-test="save-step"
          :disabled="!canSave"
          :label="t('FUNNEL_TIMELINE.SAVE')"
          @click="handleSave"
        />
      </div>
    </div>
  </div>
</template>
